import configs from '@/config'
import { createSharedFramePersistence, sharedFrameStorage } from '@/utils/indexedDB/shared-frame-cache.persistence'
import { isOfflinePresentingStoredOn } from '@/services/offlinePresentingFlag'
import Sentry, { featureTags } from '@/services/sentry'

// Bumped when a persisted response changes shape; never tied to the release build, so a deploy keeps saved copies.
export const OFFLINE_CACHE_SCHEMA_VERSION = 'v1'

// The gate for every cache entry persisted across page loads: shared between same-origin PowerPoint
// add-in frames, and the offline-presenting copies. Callers pass this as SimpleCache's
// `persistence` and check the gate before opting a call in.
export const isSharedFrameCacheEnabled = () => Boolean(configs.isEnableAddinPowerpoint) || isOfflinePresentingStoredOn()

export const sharedFramePersistence = createSharedFramePersistence({
  storage: sharedFrameStorage,
  isEnabled: isSharedFrameCacheEnabled,
  schemaVersion: OFFLINE_CACHE_SCHEMA_VERSION,
})

export const SAVED_PROFILE_KEY = 'getUser'

export const clearSavedProfiles = async () => {
  try {
    await sharedFramePersistence.removeInEveryScope(SAVED_PROFILE_KEY)
  } catch (error) {
    // Intentionally ignored: sign-out must go on when IndexedDB is unavailable.
  }
}

export const SAVED_PRESENTATION_KEY_PREFIX = 'presentationDetail:'
export const SAVED_PRESENTATIONS_INDEX_KEY = 'savedPresentations'
export const SAVED_PRESENTATION_RETENTION_SECONDS = 30 * 24 * 60 * 60

export const savedPresentationsScope = (currentUser) => `user:${currentUser.id}|workspace:${currentUser.accountId ?? ''}`

export const readSavedPresentationsIndex = async (scope) => {
  const stored = await sharedFramePersistence.read(SAVED_PRESENTATIONS_INDEX_KEY, scope)
  return Array.isArray(stored?.value) ? stored.value : []
}

export const clearSavedPresentations = async () => {
  try {
    await sharedFramePersistence.removeEveryKeyMatching((key) => key === SAVED_PRESENTATIONS_INDEX_KEY ||
      key.startsWith(SAVED_PRESENTATION_KEY_PREFIX))
  } catch (error) {
    // Sign-out must go on when IndexedDB is unavailable, but a failed purge leaves decks on a shared device.
    Sentry.captureException(error, {
      tags: featureTags({ featureFlag: 'PRE-204-offline-presenting', featureName: 'offline-presenting', taskId: 'PRE-245' }),
    })
  }
}
